import React from 'react';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../../../../../elements';
import type { Theme } from '@mui/material/styles';
import { V4_DIALOG_CONTENT, V4_DIALOG_MD, V4_ICON_BUTTON, V4_SMALL_SCREEN } from '../../../../../../theme/v4Compat';
import { AnimalTypeSelect, Loader, MedicalSupplySelectModal } from '../../../../../../elements';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import {
  MedicalSupplyBottleViewModel,
  MedicalSupplyType,
  MedicalSupplyViewModel
} from '../../../../../../models/injection';
import BottleSizeSelect from '../BottleSizeSelect';
import { handleApiError, notifications } from '../../../../../../utils';
import { MedicalSuppliesService } from '../../../../../../hooks';
import { CreateMedicalSupplyBottleCommand } from '../../../../../../models/vets';
import { AnimalType } from '../../../../../../models/animal';

interface IProps {
  opened: boolean;
  onSuccess: () => void;
  setDialog: (value: boolean) => void;
  selBottle?: MedicalSupplyBottleViewModel;
  isEditMode?: boolean;
}

interface IData {
  medicalSupply: MedicalSupplyViewModel | null;
  lotNumber: string;
  treatmentNumber: string;
  animalType: number;
  size: number;
  daysToUse: string;
  dosePerAnimal: string;
  minVolumeCC: string;
  maxVolumeCC: string;
  ccPerDose: string;
  kgPerOneCC: string;
  startVolume: string;
  incrementStep: string;
}

interface ISupplyBottleCCParams {
  minVolumeCC: string;
  maxVolumeCC: string;
  startVolume: string;
  incrementStep: string;
}

interface ICCMap {
  [key: number]: ISupplyBottleCCParams;
}

const CreateMedicalSupplyBottleDialog: React.FC<any> = ({
  opened,
  onSuccess,
  setDialog,
  selBottle,
  isEditMode
}: IProps) => {
  const styles = sxStyles(useTheme());
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);

  const antibioticCCParamsAnimalTypeMap: ICCMap = {
    [AnimalType.Piglet]: { minVolumeCC: '0.3', maxVolumeCC: '6', startVolume: '0.5', incrementStep: '0.1' },
    [AnimalType.Fattening]: { minVolumeCC: '2', maxVolumeCC: '12', startVolume: '3', incrementStep: '0.3' },
    [AnimalType.Gilt]: { minVolumeCC: '15', maxVolumeCC: '34', startVolume: '7', incrementStep: '1' },
    [AnimalType.Sow]: { minVolumeCC: '15', maxVolumeCC: '34', startVolume: '20', incrementStep: '1' },
    [AnimalType.Goat]: { minVolumeCC: '0.3', maxVolumeCC: '6', startVolume: '0.5', incrementStep: '0.1' },
    [AnimalType.Cow]: { minVolumeCC: '15', maxVolumeCC: '34', startVolume: '20', incrementStep: '1' }
  };

  const otherSupplyCCParamsAnimalTypeMap: ICCMap = {
    [AnimalType.Piglet]: { minVolumeCC: '0.5', maxVolumeCC: '6', startVolume: '2', incrementStep: '0.1' },
    [AnimalType.Fattening]: { minVolumeCC: '0.5', maxVolumeCC: '6', startVolume: '2', incrementStep: '0.1' },
    [AnimalType.Gilt]: { minVolumeCC: '0.5', maxVolumeCC: '6', startVolume: '2', incrementStep: '0.1' },
    [AnimalType.Sow]: { minVolumeCC: '0.5', maxVolumeCC: '6', startVolume: '2', incrementStep: '0.1' },
    [AnimalType.Goat]: { minVolumeCC: '0.5', maxVolumeCC: '6', startVolume: '2', incrementStep: '0.1' },
    [AnimalType.Cow]: { minVolumeCC: '0.5', maxVolumeCC: '6', startVolume: '2', incrementStep: '0.1' }
  };

  const handleDialogClose = () => {
    clearData();
    setDialog(false); // Use the prop.
  };

  const title = isEditMode ? t('f-edit-bottle') : t('f-create-new-bottle');

  const [isBusy, setIsBusy] = React.useState<boolean>(false);
  const [data, setData] = React.useState<IData>({
    medicalSupply: null,
    lotNumber: '',
    treatmentNumber: '',
    animalType: 10,
    size: 10,
    dosePerAnimal: '',
    daysToUse: '',
    minVolumeCC: '0.0',
    maxVolumeCC: '0.0',
    ccPerDose: '',
    kgPerOneCC: '',
    startVolume: '0.0',
    incrementStep: '0.0'
  });

  const clearData = () =>
    setData({
      medicalSupply: null,
      lotNumber: '',
      treatmentNumber: '',
      animalType: 10,
      size: 10,
      daysToUse: '',
      dosePerAnimal: '',
      ccPerDose: '',
      kgPerOneCC: '',
      minVolumeCC: '0.0',
      maxVolumeCC: '0.0',
      startVolume: '0.0',
      incrementStep: '0.0'
    });
  const medicalSuppliesService = new MedicalSuppliesService();

  React.useEffect(() => {
    clearData();
    if (selBottle) {
      setData({
        medicalSupply: selBottle.medicalSupply,
        lotNumber: selBottle.lotNumber,
        treatmentNumber: selBottle.treatmentNumber,
        animalType: selBottle.animalType,
        size: selBottle.size,
        daysToUse: selBottle.daysToUse.toString(),
        dosePerAnimal: selBottle.dosePerAnimal?.toString() ?? '',
        minVolumeCC: selBottle.minVolumeCC.toString(),
        maxVolumeCC: selBottle.maxVolumeCC.toString(),
        ccPerDose: selBottle.ccPerDose?.toString() ?? '',
        kgPerOneCC: selBottle.kgPerOneCC?.toString() ?? '',
        startVolume: selBottle.startVolume.toString(),
        incrementStep: selBottle.incrementStep.toString()
      });
    }
  }, [selBottle]);

  const validateForm = () => {
    if (!data.medicalSupply) {
      notifications.error(t('f-val-medical-supply'));
      return false;
    }

    if (!data.lotNumber) {
      notifications.error(t('f-val-lot-number'));
      return false;
    }

    if (parseInt(data.daysToUse) <= 0) {
      notifications.error(t('f-val-days-to-use'));
      return false;
    }

    if (!data.animalType || data.animalType === AnimalType.Unknown) {
      notifications.error(t('f-val-anim-type'));
      return false;
    }

    if (!parseInt(data.daysToUse)) {
      notifications.error(t('f-val-days-to-use'));
      return false;
    }

    return true;
  };

  const getDefaultValues = (animalType: AnimalType, supplyType: MedicalSupplyType): ISupplyBottleCCParams => {
    let values: ISupplyBottleCCParams | undefined;

    if (supplyType === MedicalSupplyType.Antibiotic) {
      values = antibioticCCParamsAnimalTypeMap[animalType];
    } else {
      values = otherSupplyCCParamsAnimalTypeMap[animalType];
    }

    if (!values) {
      values = { minVolumeCC: '0.0', maxVolumeCC: '0.0', startVolume: '0.0', incrementStep: '0.0' };
    }
    return values;
  };

  const handleSetMedicalSupply = (supply: MedicalSupplyViewModel | null) => {
    let values: ISupplyBottleCCParams | undefined;
    if (supply && data.animalType) {
      values = getDefaultValues(data.animalType, supply?.supplyType ?? MedicalSupplyType.Any);
    }
    setData({ ...data, ...values, medicalSupply: supply });
  };

  const handleSetAnimalType = (animalType: number) => {
    let values: ISupplyBottleCCParams | undefined;
    if (data.medicalSupply && animalType) {
      values = getDefaultValues(animalType, data.medicalSupply.supplyType);
    }
    setData({ ...data, ...values, animalType });
  };

  const buildBottlePayload = () => ({
    animalType: data.animalType,
    ccPerDose: parseFloat(data.ccPerDose),
    daysToUse: parseInt(data.daysToUse),
    dosePerAnimal: parseFloat(data.dosePerAnimal),
    incrementStep: parseFloat(data.incrementStep),
    kgPerOneCC: parseFloat(data.kgPerOneCC),
    lotNumber: data.lotNumber,
    maxVolumeCC: parseFloat(data.maxVolumeCC),
    minVolumeCC: parseFloat(data.minVolumeCC),
    size: data.size,
    startVolume: parseFloat(data.startVolume),
    treatmentNumber: data.treatmentNumber
  });

  const handleCreateBottle = () => {
    if (!validateForm()) {
      return;
    }

    setIsBusy(true);

    if (isEditMode && selBottle?.id) {
      medicalSuppliesService
        .updateMedicalSupplyBottle(selBottle.id, buildBottlePayload())
        .then(() => {
          notifications.success(t('f-bottle-edit-ok'));
          setIsBusy(false);
          handleDialogClose();
          onSuccess();
        })
        .catch((e) => {
          handleApiError(e);
          setIsBusy(false);
        });
    } else {
      const command: CreateMedicalSupplyBottleCommand = {
        medicalSupplyId: data.medicalSupply?.id as string,
        ...buildBottlePayload()
      };

      medicalSuppliesService
        .createMedicalSupplyBottle(command)
        .then(() => {
          notifications.success(t('f-com-created'));
          setIsBusy(false);
          handleDialogClose();
          onSuccess();
        })
        .catch((e) => {
          handleApiError(e);
          setIsBusy(false);
        });
    }
  };

  return (
    <Dialog fullWidth maxWidth="md" open={opened} fullScreen={fullScreen} onClose={handleDialogClose} sx={V4_DIALOG_MD}>
      {fullScreen && (
        <AppBar>
          <Toolbar>
            <Box sx={{ width: '100%', justifyContent: 'space-between', display: 'flex' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={() => handleDialogClose()}
                  aria-label="close"
                  sx={V4_ICON_BUTTON}
                >
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6">
                  {title}
                  {selBottle?.lotNumber}
                </Typography>
              </Box>

              <IconButton
                color="inherit"
                onClick={handleCreateBottle}
                disabled={isBusy}
                id="saveBtn"
                sx={V4_ICON_BUTTON}
              >
                <Icon>add_circle</Icon>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <DialogTitle>{title}</DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        {!isBusy ? (
          <Grid container spacing={1} direction="row">
            <Grid item lg={4} md={6} sm={6} xs={12}>
              <MedicalSupplySelectModal
                supply={data.medicalSupply}
                sx={{ m: 1, width: '100%', height: 40 }}
                onChange={(supply) => handleSetMedicalSupply(supply)}
                disableAny={true}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextField
                label={t('f-com-lot')}
                variant="outlined"
                size="small"
                sx={styles.formControl}
                value={data.lotNumber}
                slotProps={{
                  input: {
                    inputProps: { maxLength: 20 },
                    endAdornment: data.lotNumber && (
                      <InputAdornment position="end">
                        <IconButton edge="end" onClick={() => setData({ ...data, lotNumber: '' })} sx={V4_ICON_BUTTON}>
                          <Icon fontSize="small">clear</Icon>
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
                onChange={(e) => {
                  setData({ ...data, lotNumber: e.target.value });
                }}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextField
                label={t('f-com-treat-num')}
                variant="outlined"
                size="small"
                sx={styles.formControl}
                value={data.treatmentNumber}
                slotProps={{
                  input: {
                    endAdornment: data.treatmentNumber && (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setData({ ...data, treatmentNumber: '' })}
                          sx={V4_ICON_BUTTON}
                        >
                          <Icon fontSize="small">clear</Icon>
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
                onChange={(e) => {
                  setData({ ...data, treatmentNumber: e.target.value });
                }}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <AnimalTypeSelect
                value={data.animalType}
                fullWidth={true}
                cssClass="!m-2 !w-full"
                onAnimalTypeChange={(type) => handleSetAnimalType(type)}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <BottleSizeSelect
                value={data.size}
                fullWidth={true}
                cssClass="!m-2 !w-full"
                onChange={(size) => setData({ ...data, size: size })}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextField
                id="days-to-use-input"
                label={t('f-com-days-use')}
                variant="outlined"
                type="number"
                size="small"
                sx={styles.formControl}
                value={data.daysToUse}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setData({ ...data, daysToUse: newValue.toString() });
                  }
                }}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextField
                id="max-volume-cc-input"
                label={t('f-max-volume-cc')}
                variant="outlined"
                type="number"
                size="small"
                sx={styles.formControl}
                value={data.maxVolumeCC}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setData({ ...data, maxVolumeCC: newValue.toString() });
                  }
                }}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextField
                id="min-volume-cc-input"
                label={t('f-min-volume-cc')}
                variant="outlined"
                type="number"
                size="small"
                sx={styles.formControl}
                value={data.minVolumeCC}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setData({ ...data, minVolumeCC: newValue.toString() });
                  }
                }}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextField
                id="start-volume-input"
                label={t('f-start-volume')}
                variant="outlined"
                type="number"
                size="small"
                sx={styles.formControl}
                value={data.startVolume}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setData({ ...data, startVolume: newValue.toString() });
                  }
                }}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextField
                id="increment-step-input"
                label={t('f-increment-step')}
                variant="outlined"
                type="number"
                size="small"
                sx={styles.formControl}
                value={data.incrementStep}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setData({ ...data, incrementStep: newValue.toString() });
                  }
                }}
              />
            </Grid>

            <Grid item lg={4} md={6} sm={6} xs={12}>
              <TextField
                label={t('f-dose-per-animal')}
                variant="outlined"
                type="number"
                size="small"
                sx={styles.formControl}
                value={data.dosePerAnimal}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (newValue >= 0) {
                    setData({ ...data, dosePerAnimal: newValue.toString() });
                  }
                }}
              />
            </Grid>

            {[MedicalSupplyType.Antibiotic, MedicalSupplyType.Painkiller].find(
              (x) => x === data.medicalSupply?.supplyType
            ) && (
              <Grid item lg={4} md={6} sm={6} xs={12}>
                <TextField
                  id="increment-step-input"
                  label={t('f-kg-per-one-cc')}
                  variant="outlined"
                  type="number"
                  size="small"
                  sx={styles.formControl}
                  value={data.kgPerOneCC}
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    if (newValue >= 0) {
                      setData({ ...data, kgPerOneCC: newValue.toString() });
                    }
                  }}
                />
              </Grid>
            )}

            {[MedicalSupplyType.Vaccination, MedicalSupplyType.Hormones].find(
              (x) => x === data.medicalSupply?.supplyType
            ) && (
              <Grid item lg={4} md={6} sm={6} xs={12}>
                <TextField
                  label={t('f-cc-per-dose')}
                  variant="outlined"
                  size="small"
                  type="number"
                  sx={styles.formControl}
                  value={data.ccPerDose}
                  onChange={(e) => {
                    const newValue = Number(e.target.value);
                    if (newValue >= 0) {
                      setData({ ...data, ccPerDose: newValue.toString() });
                    }
                  }}
                />
              </Grid>
            )}
          </Grid>
        ) : (
          <Box sx={{ pt: 16, pb: 16 }}>
            <Loader isLoading={true} size={100} />
          </Box>
        )}
      </DialogContent>

      {!fullScreen && (
        <DialogActions>
          <Button onClick={() => handleDialogClose()} startIcon={<Icon>{'close'}</Icon>} color="primary">
            {t('f-com-cancel')}
          </Button>

          <Button
            onClick={handleCreateBottle}
            id="saveBtn"
            startIcon={<Icon>{isEditMode ? 'save' : 'add_circle_outline'}</Icon>}
            color="primary"
            disabled={isBusy}
          >
            {isEditMode ? t('f-com-save') : t('f-com-create')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CreateMedicalSupplyBottleDialog;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    formControl: {
      margin: theme.spacing(1),
      width: '100%'
    }
  }) as const;
